"use client";

import React, { useEffect, useState, useRef } from "react";
import { TrendingUp, Users, Building2, GraduationCap, ArrowUpRight, FileText, Activity, Globe } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
} = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const AnimatedCounter = ({
  value,
  prefix = "",
  suffix = "",
  duration = 2000,
  delay = 0
}: {
  value: number;
  prefix?: string;
  suffix?: string;
  duration?: number;
  delay?: number;
}) => {
  const [count, setCount] = useState(0);
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.1
  });
  useEffect(() => {
    if (!isVisible) return;
    let startTime: number | null = null;
    let animationFrameId: number;
    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = timestamp - startTime;
      const percentage = Math.min(progress / duration, 1);
      const ease = 1 - Math.pow(1 - percentage, 4);
      setCount(value * ease);
      if (progress < duration) {
        animationFrameId = requestAnimationFrame(animate);
      }
    };
    const timeoutId = setTimeout(() => {
      animationFrameId = requestAnimationFrame(animate);
    }, delay);
    return () => {
      clearTimeout(timeoutId);
      cancelAnimationFrame(animationFrameId);
    };
  }, [isVisible, value, duration, delay]);
  const formattedCount = count % 1 === 0 ? Math.floor(count).toLocaleString() : count.toFixed(1);
  return <span ref={ref} className="tabular-nums">
      {prefix}{formattedCount}{suffix}
    </span>;
};
interface MetricCardProps {
  title: string;
  value: string | React.ReactNode;
  description: string;
  icon: React.ElementType;
  className?: string;
  glow?: boolean;
  delay?: number;
  linkTo?: string;
}
const MetricCard = ({
  title,
  value,
  description,
  icon: Icon,
  className,
  glow = false,
  delay = 0,
  linkTo = "/DataResearchPage"
}: MetricCardProps) => {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.1
  });
  return <div ref={ref as React.RefObject<HTMLDivElement>} className={cn("group relative overflow-hidden rounded-3xl border p-6 transition-all duration-500", "bg-card/10 backdrop-blur-md hover:bg-card/20", glow ? "border-chart-1/50 shadow-[0_0_40px_-10px_var(--color-chart-1)] hover:shadow-[0_0_60px_-5px_var(--color-chart-1)]" : "border-white/10 hover:border-white/20 shadow-lg", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8", className)} style={{
    transitionDelay: `${delay}ms`
  }}>
      {}
      <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/60 opacity-0 backdrop-blur-sm transition-opacity duration-300 group-hover:opacity-100">
        <Link to={linkTo} className="no-underline">
          <Button variant="outline" className="border-white/20 bg-white/10 text-white hover:bg-white/20 hover:text-white">
            Read Report <ArrowUpRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </div>

      {}
      <div className="relative z-10 flex h-full flex-col justify-between">
        <div className="mb-4 flex items-start justify-between">
          <div className={cn("rounded-full p-3", glow ? "bg-chart-1/20 text-chart-1" : "bg-white/5 text-muted-foreground group-hover:text-white")}>
            <Icon className="h-6 w-6" />
          </div>
          {glow && <div className="flex items-center gap-2 rounded-full bg-chart-1/10 px-3 py-1 text-xs font-medium text-chart-1 animate-pulse">
              <Activity className="h-3 w-3" />
              Live Data
            </div>}
        </div>

        <div>
          <h3 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
            {title}
          </h3>
          <div className={cn("mt-2 font-bold tracking-tight", glow ? "text-5xl md:text-6xl text-white drop-shadow-[0_0_15px_rgba(var(--color-chart-1),0.5)]" : "text-3xl md:text-4xl text-white")}>
            {value}
          </div>
          <p className="mt-2 text-sm text-muted-foreground/80 group-hover:text-muted-foreground">
            {description}
          </p>
        </div>
      </div>

      {}
      <div className="absolute -right-12 -top-12 h-40 w-40 rounded-full bg-gradient-to-br from-white/5 to-transparent blur-3xl transition-transform duration-700 group-hover:scale-150" />
    </div>;
};
export default function EconomicImpactDashboard() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll({
    once: true
  });
  return <section id="economic-impact-dashboard" className="dark bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
      {}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] h-[600px] w-[600px] rounded-full bg-indigo-900/20 blur-[100px]" />
        <div className="absolute bottom-[-10%] left-[-5%] h-[500px] w-[500px] rounded-full bg-blue-950/30 blur-[100px]" />
        <div className="absolute left-1/2 top-1/2 h-[800px] w-[800px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-slate-900/50 blur-3xl" />
      </div>

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        {}
        <div ref={headerRef as React.RefObject<HTMLDivElement>} className={cn("mb-16 max-w-3xl transition-all duration-700", headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <h2 className="text-4xl font-bold tracking-tight text-white sm:text-5xl mb-6">
            Economic <span className="text-transparent bg-clip-text bg-gradient-to-r from-chart-1 to-indigo-400">Intelligence</span>
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Real-time insights into the Hispanic market's explosive growth. 
            We track the metrics that matter to Colorado's economy, providing data-driven 
            clarity for investors, policymakers, and business leaders.
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3 md:grid-rows-2 lg:gap-8">
          
          {}
          <div className="md:col-span-2 md:row-span-2">
            <MetricCard title="U.S. Hispanic Purchasing Power" value={<div className="flex items-baseline gap-1">
                  <span className="text-chart-1">$</span>
                  <AnimatedCounter value={3.2} duration={2500} />
                  <span className="text-3xl md:text-4xl text-muted-foreground/60 font-light">Trillion</span>
                </div>} description="The U.S. Latino GDP is growing 2.5x faster than the non-Latino equivalent, representing a massive economic engine." icon={TrendingUp} className="h-full min-h-[300px]" glow={true} delay={100} />
          </div>

          {}
          <div className="md:col-span-1 md:row-span-1">
            <MetricCard title="Hispanic-Owned Businesses" value={<div className="flex items-baseline">
                  <AnimatedCounter value={120} duration={2000} delay={200} />
                  <span className="text-chart-2">,</span>
                  <AnimatedCounter value={0} duration={1000} delay={1000} prefix="00" />
                  <span className="text-xl text-chart-2 ml-1">+</span>
                </div>} description="Driving entrepreneurship across Colorado." icon={Building2} delay={200} />
          </div>

          {}
          <div className="md:col-span-1 md:row-span-1">
            <MetricCard title="State Purchasing Power" value={<div className="flex items-baseline gap-1">
                  <span className="text-chart-2">$</span>
                  <AnimatedCounter value={40} duration={2000} delay={300} />
                  <span className="text-2xl text-muted-foreground/60">Billion+</span>
                </div>} description="Annual economic contribution in Colorado." icon={FileText} delay={300} />
          </div>

          {}
          <div className="md:col-span-1 md:row-span-1">
            <MetricCard title="Market Rank" value={<div className="flex items-baseline gap-1">
                  <span className="text-chart-3">#</span>
                  <AnimatedCounter value={8} duration={1500} delay={400} />
                </div>} description="Largest Hispanic market in the United States." icon={Globe} delay={400} />
          </div>

          {}
          <div className="md:col-span-1 md:row-span-1">
            <MetricCard title="Future Workforce" value={<div className="flex items-baseline gap-1">
                  <AnimatedCounter value={58} duration={2000} delay={500} />
                  <span className="text-chart-4">%</span>
                </div>} description="Of Denver Public Schools students are Hispanic." icon={GraduationCap} delay={500} />
          </div>

          {}
          <div className="md:col-span-1 md:row-span-1">
             <div className="group relative flex h-full flex-col items-center justify-center overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-chart-1/20 to-indigo-900/20 p-6 text-center backdrop-blur-md transition-all duration-300 hover:border-chart-1/30 hover:from-chart-1/30 hover:to-indigo-900/30">
                <div className="relative z-10">
                  <h3 className="mb-2 text-xl font-bold text-white">Access Full Report</h3>
                  <p className="mb-6 text-sm text-muted-foreground">
                    Download the comprehensive 2024 Economic Impact Study.
                  </p>
                  <Link to="/DataResearchPage">
                    <Button className="w-full bg-white text-black hover:bg-white/90">
                      View Dashboard <ArrowUpRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
                {}
                <div className="absolute inset-0 bg-gradient-to-t from-chart-1/10 via-transparent to-transparent opacity-50" />
             </div>
          </div>

        </div>
      </div>
    </section>;
}